import { Injectable } from '@angular/core';
import { getJobsiteSelected, OrderBuilderState } from '@ordersnproducts/modules/order-builder/reducers';
import { Store } from '@ngrx/store';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { filter, take, tap } from 'rxjs/operators';
import { OnFieldChanged } from '../../configurable-form/models/on-field-changed';
import { LoadPrimerSlumpValues, LoadPumpPrimerValues } from '../redux/pump-primer.actions';
import { PUMP_NONE_CODE } from 'app/shared/pumping/pumping.constant';
import { UntypedFormGroup } from '@angular/forms';
import { ConfigurableFormComponent } from '../../configurable-form/containers/configurable-form/configurable-form.component';
import { FieldConfig } from '../../configurable-form/models/field-configs/field-config.interface';
import { getRmsRecipeSpecifications } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import { CxModalService } from 'app/shared/cx-modal/cx-modal.service';
import { RemoveProductsDialogComponent } from '@ordersnproducts/modules/shared/components/remove-products-dialog/remove-products-dialog.component';
import { Subject } from 'rxjs';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { WorkflowDcoService } from '@ordersnproducts/modules/shared/components/dco/workflow/services/workflow-dco.service';

@Injectable()
export class LoadPumpPrimerValuesService implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(value: any, formGroup: UntypedFormGroup, form: ConfigurableFormComponent): void {
    this.store
      .select(getJobsiteSelected)
      .pipe(filter((jobsite) => !!jobsite))
      .subscribe(({ shipmentLocationCode }) => {
        this.store.dispatch(new LoadPumpPrimerValues(shipmentLocationCode));
      });
  }
}

@Injectable()
export class LoadPrimerSlumpService implements OnFieldChanged {
  constructor(private store: Store<OrderBuilderState>) {}

  onFieldChanged(productCode: number | string): void {
    if (!!productCode && productCode !== PUMP_NONE_CODE) {
      this.store.dispatch(new LoadPrimerSlumpValues(productCode));
    }
  }
}

/**
 * Service to show a warning of removing additional products when user selects a pump primer product
 */
@Injectable()
export class PumpPrimerMixRemoveProductWarnService implements OnFieldChanged {
  constructor(
    private readonly store: Store<OrderBuilderState>,
    private readonly cxModalService: CxModalService,
    private workflowDcoService: WorkflowDcoService,
  ) {}

  public removeAdditionalProducts: Subject<number> = new Subject<number>();

  onFieldChanged(value: any, formGroup: UntypedFormGroup, form: ConfigurableFormComponent, config?: FieldConfig): void {
    this.store
      .select(getRmsRecipeSpecifications)
      .pipe(
        take(1),
        filter(
          (rmsRecipeSpecifications) =>
            !!value && value !== PUMP_NONE_CODE && !!rmsRecipeSpecifications && rmsRecipeSpecifications.length > 0,
        ),
        tap((rmsRecipeSpecifications) => {
          const recipe = rmsRecipeSpecifications.find((x) => x.id === formGroup.get('id').value);
          const oldValue = recipe.values[SpecFieldNames.PUMP_PRIMER_PRODUCT];
          const index = rmsRecipeSpecifications.indexOf(recipe);
          if (rmsRecipeSpecifications.length > 1 && index === 0 && oldValue !== value) {
            const modalInstance = this.cxModalService.createModal(RemoveProductsDialogComponent, {
              inputs: { rtl: form.rtl },
              outputs: {
                close: () => {
                  this.workflowDcoService.ignoreDcoChangesNotification$.next(true);
                  formGroup.get(SpecFieldNames.PUMP_PRIMER_PRODUCT).setValue(oldValue);
                  this.cxModalService.removeModal(modalInstance);
                },
                accept: () => {
                  this.removeAdditionalProducts.next(recipe.id);
                  this.workflowDcoService.ignoreDcoChangesNotification$.next(true);
                  this.workflowDcoService.resetDcoByWarningModal$.next(recipe.id);
                  this.cxModalService.removeModal(modalInstance);
                },
                cancel: () => {
                  this.workflowDcoService.ignoreDcoChangesNotification$.next(true);
                  formGroup.get(SpecFieldNames.PUMP_PRIMER_PRODUCT).setValue(oldValue);
                  this.cxModalService.removeModal(modalInstance);
                },
              },
            });
          }
        }),
      )
      .subscribe();
  }
}
